import type { Narrator } from "@serophin/nottldr-shared";
import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { type AudioPlayer, AudioPlayerContext, type CurrentArticle } from "../AudioPlayerContext";
import { useToast } from "../ToastContext";
import { audioUrl, useAudioSettings } from "./audioSettings";
import { bindMediaActions, setMediaMetadata } from "./mediaSession";
import { type Engine, useAudioEngine } from "./useAudioEngine";
import { useListening } from "./useListening";
import { type Playlist, scrollToCard, usePlaylist } from "./usePlaylist";
import { useProgressDrag } from "./useProgressDrag";

const SKIP_SECONDS = 10;

/** What the engine's callbacks (made once) read to see the player as it is now. */
type Live = { current: CurrentArticle | null; narrator: Narrator; speed: number; next: () => void; previous: () => void };

type Core = {
  engine: Engine;
  playlist: Playlist;
  current: CurrentArticle | null;
  setCurrent: (article: CurrentArticle) => void;
  live: { current: Live };
  narrator: Narrator;
  speed: number;
  cycleSpeed: () => void;
};

const useCore = (): Core => {
  const toast = useToast();
  const { narrator, speed, cycleSpeed } = useAudioSettings();
  const playlist = usePlaylist();
  const [current, setCurrent] = useState<CurrentArticle | null>(null);
  const live = useRef<Live>({ current, narrator, speed, next: () => {}, previous: () => {} });
  live.current = { ...live.current, current, narrator, speed };

  const nextUrl = () => {
    const next = playlist.neighbour(live.current.current, 1);
    return next ? audioUrl(next.authorAlias, next.id, narrator) : null;
  };
  const engine = useAudioEngine({
    rate: () => live.current.speed,
    onTime: useListening(current, nextUrl),
    onLoaded: () => live.current.current && setMediaMetadata(live.current.current.title, live.current.current.authorAlias),
    onEnded: () => live.current.next(),
    onError: () => toast.error("Failed to load audio. Please try again."),
  });
  return { engine, playlist, current, setCurrent, live, narrator, speed, cycleSpeed };
};

/** Starting a track, play and pause of the current one, and moving along the playlist. */
const useTransport = ({ engine, playlist, current, setCurrent, live, narrator, speed }: Core) => {
  const play = async (authorAlias: string, articleId: string, title?: string) => {
    live.current.current = { authorAlias, articleId, title };
    setCurrent(live.current.current);
    engine.load(audioUrl(authorAlias, articleId, narrator));
  };

  const togglePlayPause = async (authorAlias: string, articleId: string, title?: string) => {
    const same = current?.authorAlias === authorAlias && current.articleId === articleId;
    if (engine.loadingState === "loading") {
      return;
    }
    if (!same || engine.loadingState !== "loaded") {
      await play(authorAlias, articleId, title);
    } else if (engine.isPlaying) {
      engine.pause();
    } else {
      engine.resume(speed);
    }
  };

  const step = (offset: number) => {
    const target = playlist.neighbour(live.current.current, offset);
    if (target) {
      void play(target.authorAlias, target.id, target.title);
      scrollToCard(target);
    }
  };
  live.current.next = () => step(1);
  live.current.previous = () => step(-1);
  return { togglePlayPause };
};

/** The speed reaches the element, the lock screen gets its buttons, and the tab's title says what plays. */
const useSystemSides = ({ engine, live, speed, current }: Core): void => {
  const title = useRef("");
  useEffect(() => engine.setRate(speed), [speed]);
  useEffect(
    () =>
      bindMediaActions(() => ({
        play: () => engine.resume(live.current.speed),
        pause: engine.pause,
        next: () => live.current.next(),
        previous: () => live.current.previous(),
      })),
    [],
  );
  useEffect(() => {
    if (engine.isPlaying && current?.title) {
      title.current = document.title;
      document.title = `${current.title} by ${current.authorAlias} | !tldr`;
      return () => {
        document.title = title.current;
      };
    }
  }, [engine.isPlaying, current?.authorAlias, current?.articleId]);
};

/**
 * The site's one audio player: a single `<audio>` element, a playlist (the articles on screen) that moves on by itself
 * and fetches the next track ahead, the lock screen's controls, the speed and narrator settings, and the 80 % read mark.
 */
export const AudioPlayerProvider = ({ children }: { children: ComponentChildren }) => {
  const core = useCore();
  const { engine, current, playlist } = core;
  const { togglePlayPause } = useTransport(core);
  const drag = useProgressDrag(engine.duration, engine.seek);
  useSystemSides(core);

  const value: AudioPlayer = {
    currentArticle: current,
    isPlaying: engine.isPlaying,
    loadingState: engine.loadingState,
    currentTime: engine.currentTime,
    duration: engine.duration,
    playbackSpeed: core.speed,
    isArticlePlaying: (authorAlias, articleId) => engine.isPlaying && current?.authorAlias === authorAlias && current.articleId === articleId,
    togglePlayPause,
    setPlaylist: playlist.setPlaylist,
    pause: engine.pause,
    skipBackward: (seconds = SKIP_SECONDS) => engine.skip(-seconds),
    skipForward: (seconds = SKIP_SECONDS) => engine.skip(seconds),
    cyclePlaybackSpeed: core.cycleSpeed,
    ...drag,
  };

  return <AudioPlayerContext.Provider value={value}>{children}</AudioPlayerContext.Provider>;
};
